// src/letter-state.ts
var LETTER_STATES = [
  "draft",
  "sealed",
  "in_transit",
  "delivered",
  "opened",
  "answered",
  "cancelled"
];
var LETTER_TRIGGERS = [
  "seal",
  "release",
  "notify",
  "open",
  "answer",
  "cancel"
];
var LETTER_TRANSITIONS = {
  draft: { seal: "sealed", release: null, notify: null, open: null, answer: null, cancel: "cancelled" },
  sealed: { seal: null, release: "in_transit", notify: null, open: null, answer: null, cancel: "cancelled" },
  // The sender's cooldown ends at deliver_at: once the scheduler has released
  // the letter, it is in flight and cannot be stopped (§7.x cooldown).
  in_transit: { seal: null, release: null, notify: "delivered", open: null, answer: null, cancel: null },
  delivered: { seal: null, release: null, notify: null, open: "opened", answer: null, cancel: null },
  opened: { seal: null, release: null, notify: null, open: null, answer: "answered", cancel: null },
  answered: { seal: null, release: null, notify: null, open: null, answer: null, cancel: null },
  cancelled: { seal: null, release: null, notify: null, open: null, answer: null, cancel: null }
};
function nextState(state, trigger) {
  return LETTER_TRANSITIONS[state][trigger];
}
function isTerminal(state) {
  return Object.values(LETTER_TRANSITIONS[state]).every((next) => next === null);
}
function isVisibleToRecipient(state) {
  return state === "delivered" || state === "opened" || state === "answered";
}
var SETTLED_STATES = [
  "delivered",
  "opened",
  "answered",
  "cancelled"
];
function isPeekable(state) {
  return state === "sealed" || state === "in_transit";
}

// src/overture.ts
var LETTER_KINDS = ["letter", "overture", "system-letter"];
function isOvertureKind(kind) {
  return kind === "overture";
}
var OVERTURE_DECISIONS = ["accepted", "declined"];
function isOvertureDecided(decision) {
  return decision === "accepted" || decision === "declined";
}
function isOvertureClosed(decision) {
  return decision === "declined";
}

// src/post-office.ts
var SYSTEM_LETTER_KINDS = ["welcome", "unlock", "digest", "year", "feature"];
function isSystemLetterKind(value) {
  return value != null && SYSTEM_LETTER_KINDS.includes(value);
}
var FEATURE_KEYS = ["invites", "postage", "vault", "photos", "birthday"];
function isFeatureKey(value) {
  return value != null && FEATURE_KEYS.includes(value);
}

// src/zoned-time.ts
var HOUR = 60 * 60 * 1e3;
var formatters = /* @__PURE__ */ new Map();
function formatter(timeZone) {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
    formatters.set(timeZone, f);
  }
  return f;
}
function isValidTimeZone(timeZone) {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}
function wallClock(at, timeZone) {
  const parts = {};
  for (const p of formatter(timeZone).formatToParts(at)) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  const year = parts.year;
  const month = parts.month;
  const day = parts.day;
  return {
    year,
    month,
    day,
    hour: parts.hour % 24,
    minute: parts.minute,
    second: parts.second,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  };
}
function offsetMs(at, timeZone) {
  const w = wallClock(at, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(at.getTime() / 1e3) * 1e3;
}
function zonedInstant(year, month, day, hour, minute, timeZone) {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const candidates = [
    naive - offsetMs(new Date(naive - 12 * HOUR), timeZone),
    naive - offsetMs(new Date(naive + 12 * HOUR), timeZone)
  ];
  const valid = candidates.filter((t) => {
    const w = wallClock(new Date(t), timeZone);
    const want = new Date(naive);
    return w.year === want.getUTCFullYear() && w.month === want.getUTCMonth() + 1 && w.day === want.getUTCDate() && w.hour === hour && w.minute === minute;
  });
  return new Date(valid.length > 0 ? Math.min(...valid) : Math.max(...candidates));
}
function addLocalDays(w, n) {
  const d = new Date(Date.UTC(w.year, w.month - 1, w.day + n));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), weekday: d.getUTCDay() };
}

// src/delivery.ts
var DELIVERY_BOUNDS = {
  /** Correctness floor. Never configurable downward — see assertBilateralHold. */
  MIN_HOLD_MS: 20 * 60 * 1e3,
  /** Product dial: the longest a letter is ever held. */
  MAX_HOLD_MS: 24 * 60 * 60 * 1e3
};
var DEFAULT_HOLD_MS = 4 * 60 * 60 * 1e3;
var POST_TIMEZONE = "Europe/London";
var COLLECTION_WINDOWS = [
  { hour: 9, minute: 0 },
  { hour: 13, minute: 0 },
  { hour: 17, minute: 0 }
];
var COLLECTION_DAYS = [1, 2, 3, 4, 5, 6];
var DELIVERY_HOURS = { start: { hour: 8, minute: 0 }, end: { hour: 17, minute: 0 } };
var DEFAULT_DELIVERY_CONFIG = {
  minHoldMs: DELIVERY_BOUNDS.MIN_HOLD_MS,
  maxHoldMs: DELIVERY_BOUNDS.MAX_HOLD_MS,
  defaultHoldMs: DEFAULT_HOLD_MS,
  collectionWindows: COLLECTION_WINDOWS,
  collectionDays: COLLECTION_DAYS,
  deliveryStart: DELIVERY_HOURS.start,
  deliveryEnd: DELIVERY_HOURS.end,
  timeZone: POST_TIMEZONE
};
function sortedWindows(cfg) {
  return [...cfg.collectionWindows].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
}
function nextCollectionTime(from, cfg = DEFAULT_DELIVERY_CONFIG) {
  const days = new Set(cfg.collectionDays);
  const windows = sortedWindows(cfg);
  const today = wallClock(from, cfg.timeZone);
  for (let n = 0; n < 9; n++) {
    const date = addLocalDays(today, n);
    if (!days.has(date.weekday)) continue;
    for (const { hour, minute } of windows) {
      const slot = zonedInstant(date.year, date.month, date.day, hour, minute, cfg.timeZone);
      if (slot.getTime() > from.getTime()) return slot;
    }
  }
  throw new Error("nextCollectionTime: no collection slot found within 8 days");
}
function nextDeliveryTime(at, cfg = DEFAULT_DELIVERY_CONFIG) {
  const days = new Set(cfg.collectionDays);
  const start = cfg.deliveryStart.hour * 60 + cfg.deliveryStart.minute;
  const end = cfg.deliveryEnd.hour * 60 + cfg.deliveryEnd.minute;
  if (end <= start || days.size === 0) return new Date(at);
  const local = wallClock(at, cfg.timeZone);
  const minuteOfDay = local.hour * 60 + local.minute + local.second / 60 + at.getTime() % 1e3 / 6e4;
  const postDay = days.has(local.weekday);
  if (postDay && minuteOfDay >= start && minuteOfDay <= end) return new Date(at);
  for (let n = postDay && minuteOfDay < start ? 0 : 1; n < 9; n++) {
    const date = addLocalDays(local, n);
    if (days.has(date.weekday)) {
      return zonedInstant(date.year, date.month, date.day, cfg.deliveryStart.hour, cfg.deliveryStart.minute, cfg.timeZone);
    }
  }
  return new Date(at);
}
function isPastLastPost(now, cfg = DEFAULT_DELIVERY_CONFIG) {
  const local = wallClock(now, cfg.timeZone);
  if (!cfg.collectionDays.includes(local.weekday)) return true;
  const last = sortedWindows(cfg).at(-1);
  if (!last) return true;
  return local.hour * 60 + local.minute >= last.hour * 60 + last.minute;
}
var QUALITY_MODULATION = 0.15;
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function computeDeliveryWindow(sealedAt, signals = {}, cfg = DEFAULT_DELIVERY_CONFIG) {
  const minHold = Math.max(cfg.minHoldMs, DELIVERY_BOUNDS.MIN_HOLD_MS);
  const maxHold = Math.max(cfg.maxHoldMs, minHold);
  const defaultHold = clamp(cfg.defaultHoldMs, minHold, maxHold);
  const postage = signals.postage;
  const collectedAt = postage?.collection === "immediate" ? new Date(sealedAt) : nextCollectionTime(sealedAt, cfg);
  const cap = Math.max(minHold, Math.min(maxHold, postage?.maxHoldMs ?? maxHold));
  const factor = postage ? postage.classFactor : signals.tierFactor ?? 1;
  if (signals.analysisUnavailable || signals.medianExchangeIntervalMs == null) {
    const holdMs2 = postage?.appliesToDefault ? clamp(defaultHold * factor, minHold, cap) : clamp(defaultHold, minHold, cap);
    return delivered(collectedAt, holdMs2, true, cfg);
  }
  const pace = signals.medianExchangeIntervalMs * 0.5;
  const quality = signals.qualityScore == null ? 0 : (signals.qualityScore - 0.5) * 2 * QUALITY_MODULATION;
  const holdMs = clamp(pace * (1 + quality) * factor, minHold, cap);
  return delivered(collectedAt, holdMs, false, cfg);
}
function delivered(collectedAt, holdMs, degraded, cfg) {
  const held = new Date(collectedAt.getTime() + holdMs);
  const deliverAt = nextDeliveryTime(held, cfg);
  return { collectedAt, deliverAt, holdMs, roundWaitMs: deliverAt.getTime() - held.getTime(), degraded };
}
var BilateralHoldViolated = class extends Error {
  constructor(holdMs) {
    super(
      `Bilateral delay violated: hold of ${holdMs}ms is below MIN_HOLD_MS (${DELIVERY_BOUNDS.MIN_HOLD_MS}ms). Every letter must be held in both directions \u2014 see REWRITE_PLAN.md \xA77.2.`
    );
    this.holdMs = holdMs;
    this.name = "BilateralHoldViolated";
  }
  holdMs;
};
function assertBilateralHold(sealedAt, deliverAt) {
  const holdMs = deliverAt.getTime() - sealedAt.getTime();
  if (holdMs < DELIVERY_BOUNDS.MIN_HOLD_MS) {
    throw new BilateralHoldViolated(holdMs);
  }
}

// src/judgement.ts
import { z } from "zod";
var key = z.string().min(1).max(64).regex(/^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)*$/);
var token = z.string().min(1).max(24).regex(/^[a-z][a-z0-9_]*$/, "must be a lowercase token, not a sentence");
var NoulJudgement = z.object({
  kind: z.literal("noul"),
  key,
  verdict: z.enum(["yes", "no", "unknown"]),
  confidence: z.number().min(0).max(1)
});
var ScoreJudgement = z.object({
  kind: z.literal("score"),
  key,
  verdict: token,
  confidence: z.number().min(0).max(1)
});
var ChoiceJudgement = z.object({
  kind: z.literal("choice"),
  key,
  verdict: token,
  confidence: z.number().min(0).max(1)
});
var Judgement = z.discriminatedUnion("kind", [NoulJudgement, ScoreJudgement, ChoiceJudgement]);
var LetterAnalysis = z.object({
  letterId: z.string().min(1),
  /** Stored as letters.algorithm_version so scores stay comparable. */
  modelVersion: z.string().min(1),
  judgements: z.array(Judgement).max(50)
});
function parseJudgements(raw) {
  const result = LetterAnalysis.safeParse(raw);
  if (!result.success) return [];
  return result.data.judgements;
}
function usableJudgements(judgements, minConfidence) {
  return judgements.filter((j) => j.confidence >= minConfidence);
}
function judgementValue(j) {
  switch (j.kind) {
    case "noul":
      if (j.verdict === "unknown") return 0.5;
      return j.verdict === "yes" ? 1 : 0;
    case "score":
      return SCORE_LEVEL_VALUES[j.verdict] ?? 0.5;
    case "choice":
      return CHOICE_VALUES[j.verdict] ?? 0.5;
  }
}
var SCORE_LEVEL_VALUES = {
  none: 0,
  minimal: 0.25,
  partial: 0.5,
  strong: 0.75,
  complete: 1
};
var CHOICE_VALUES = {};

// src/explain.ts
var NUMERIC_COPY = {
  "length.ratioToPrior": {
    low: "This is quite a bit shorter than the letter you're answering. They wrote you a long one, so you might like to add a little more.",
    high: "This is longer than the letter you're answering, which is completely fine."
  },
  "caps.ratio": {
    high: "There are a lot of capitals in here. They can read as shouting, even when you don't mean them to."
  },
  "time.sealedAtHour": {
    late: "You're sealing this late at night. It might be worth one more read in the morning before it goes."
  }
};
var JUDGEMENT_COPY = {
  "reciprocity.askedQuestion": {
    no: "You haven't asked them anything this time. A question gives them something to write back to."
  },
  "reciprocity.referencedPriorLetter": {
    no: "This doesn't pick up on anything from their last letter. A line about something they told you can go a long way."
  },
  // Keyed by the rubric's three levels (SCORE_LEVELS). `partial` already means
  // "some details unique to this person", so it gets no comment.
  "specificity": {
    minimal: "This could almost be written to anyone. A detail only the two of you share would make it feel like theirs."
  },
  // Engagement is judged on first letters too, so its copy must not assume
  // there was a letter to answer.
  "engagement": {
    minimal: "This one feels a little rushed. There's no hurry, so take your time if you'd like to add more."
  }
};
function explain(judgements) {
  const out = [];
  for (const j of judgements) {
    const copy = JUDGEMENT_COPY[j.key]?.[j.verdict];
    if (!copy) continue;
    out.push({
      key: j.key,
      severity: judgementValue(j) < 0.35 ? "note" : "worth-noting",
      message: copy
    });
  }
  return out;
}
function explainNumeric(key2, level) {
  const message = NUMERIC_COPY[key2]?.[level];
  if (!message) return null;
  return { key: key2, severity: "note", message };
}

// src/quality.ts
var ALGORITHM_VERSION = "quality-v1";
var DEFAULT_MIN_CONFIDENCE = 0.6;
var SCORE_LEVELS = ["minimal", "partial", "complete"];
var RUBRIC = [
  {
    key: "specificity",
    kind: "score",
    prompt: "How specific is this letter to its recipient? A letter that could be sent to anyone scores minimal.",
    criteria: {
      minimal: "Could be sent to anyone \u2014 no person-specific detail.",
      partial: "Some details unique to this person or relationship.",
      complete: "Uniquely about this person; a stranger could not have written it."
    }
  },
  {
    key: "engagement",
    kind: "score",
    prompt: "How engaged does the writer sound with the recipient? Not length \u2014 investment of attention.",
    criteria: {
      minimal: "No real investment of attention; a form letter.",
      partial: "Somewhat engaged with the recipient.",
      complete: "Deeply engaged; the letter itself is an act of attention."
    }
  },
  {
    key: "reciprocity.askedQuestion",
    kind: "noul",
    prompt: "Does the letter invite a response \u2014 ask a question, request something, or otherwise reach toward the recipient?"
  },
  {
    key: "reciprocity.referencedPriorLetter",
    kind: "noul",
    prompt: "Does the letter reference the previous letter or the ongoing exchange between these two people?"
  },
  {
    // Shown on the recipient's peek (§7.x) — a tone label, deliberately NOT
    // weighted into the quality composite: it describes the letter, it does not
    // grade it.
    key: "tone.sentiment",
    kind: "choice",
    prompt: "What is the letter's overall emotional tone toward the recipient?",
    criteria: {
      warm: "Affectionate or kind toward the recipient.",
      neutral: "Matter-of-fact.",
      tense: "Strained or sharp."
    }
  }
];
var QUALITY_WEIGHTS = {
  specificity: 0.4,
  engagement: 0.35,
  "reciprocity.referencedPriorLetter": 0.25
};
var CAPS_PENALTY_FACTOR = 0.9;
var CAPS_PENALTY_RATIO = 0.5;
function computeComposite(judgements, minConfidence, numeric = {}) {
  const usable = usableJudgements(judgements, minConfidence);
  const usedKeys = usable.map((j) => j.key).filter((k) => QUALITY_WEIGHTS[k] != null);
  if (usedKeys.length === 0) {
    return { composite: null, usedKeys: [], degraded: true };
  }
  let numerator = 0;
  let denominator = 0;
  for (const key2 of usedKeys) {
    const weight = QUALITY_WEIGHTS[key2];
    if (weight == null) continue;
    const j = usable.find((u) => u.key === key2);
    numerator += judgementValue(j) * weight;
    denominator += weight;
  }
  let composite = numerator / denominator;
  const capsRatio = numeric.capsRatio ?? 0;
  if (capsRatio > CAPS_PENALTY_RATIO) composite *= CAPS_PENALTY_FACTOR;
  composite = Math.round(composite * 100) / 100;
  return { composite, usedKeys, degraded: false };
}
function numericSignals(body) {
  const words2 = body.trim().split(/\s+/).filter(Boolean);
  const caps = words2.filter((w) => /^[A-Z][^a-z]*$/.test(w)).length;
  return {
    wordCount: words2.length,
    capsRatio: words2.length === 0 ? 0 : caps / words2.length
  };
}

// src/relationship.ts
var RELATIONSHIP_TIERS = ["cold", "new", "tepid", "hot", "trusted"];
var TIER_ORDER = {
  cold: 0,
  new: 1,
  tepid: 2,
  hot: 3,
  trusted: 4
};
var TIER_LABELS = {
  cold: "quiet",
  new: "just started",
  tepid: "finding its rhythm",
  hot: "close",
  trusted: "in trust"
};
var TIER_RULES_VERSION = "relationship-v1";
var CAPABILITIES = ["contact", "photos", "history"];
var TIER_FACTORS = {
  trusted: 0.75,
  hot: 0.9,
  new: 1.2,
  tepid: 1.4,
  cold: 1.8
};
var DAY = 24 * 60 * 60 * 1e3;
var TIER_THRESHOLDS = {
  NEW_MIN_EXCHANGES: 1,
  /** A pair with any activity inside this window is not "new-complete" for hot. */
  HOT_MAX_RESPONSE_MS: 1 * DAY,
  HOT_MAX_INTERVAL_MS: 7 * DAY,
  TRUSTED_MIN_EXCHANGES: 3,
  TRUSTED_MAX_RESPONSE_MS: 3 * DAY,
  TRUSTED_MIN_COMPOSITE: 0.6,
  TRUSTED_MIN_RECIPROCITY: 0.4,
  COLD_SILENCE_DAYS: 30,
  COLD_MAX_RECIPROCITY: 0.2
};
var REASON_KEYS = {
  coldSilence: "quiet.long-silence",
  coldOneSided: "quiet.one-sided",
  newFirstExchange: "new.first-exchange",
  hotActive: "hot.active-exchange",
  tepidDefault: "tepid.still-learning",
  trustedSummary: "trusted.sustained-exchange"
};
var RELATIONSHIP_REASON_COPY = {
  [REASON_KEYS.coldSilence]: "No letters in a while \u2014 a short one would restart things.",
  [REASON_KEYS.coldOneSided]: "The writing is mostly one-way right now.",
  [REASON_KEYS.newFirstExchange]: "Waiting on the first letter back.",
  [REASON_KEYS.hotActive]: "You two write to each other a lot, and the replies come quickly.",
  [REASON_KEYS.tepidDefault]: "You've started writing to each other \u2014 the rhythm is still finding itself.",
  [REASON_KEYS.trustedSummary]: "Your letters have earned some weight \u2014 you write back and forth, and the replies come."
};
function tenseMajority(tone) {
  const total = tone.warm + tone.neutral + tone.tense;
  return total > 0 && tone.tense > tone.warm + tone.neutral;
}
function classifyTier(signals) {
  const { letterCount, completedExchanges, daysSinceActivity, reciprocity } = signals;
  const t = TIER_THRESHOLDS;
  const reasons = [];
  if (letterCount >= 1 && daysSinceActivity != null && daysSinceActivity > t.COLD_SILENCE_DAYS) {
    reasons.push(REASON_KEYS.coldSilence);
    return { tier: "cold", reasons };
  }
  if (completedExchanges >= 2 && reciprocity != null && reciprocity < t.COLD_MAX_RECIPROCITY) {
    reasons.push(REASON_KEYS.coldOneSided);
    return { tier: "cold", reasons };
  }
  if (completedExchanges < t.NEW_MIN_EXCHANGES) {
    reasons.push(REASON_KEYS.newFirstExchange);
    return { tier: "new", reasons };
  }
  if (completedExchanges >= t.TRUSTED_MIN_EXCHANGES && signals.meanComposite != null && signals.meanComposite >= t.TRUSTED_MIN_COMPOSITE && (signals.medianResponseLatencyMs == null || signals.medianResponseLatencyMs <= t.TRUSTED_MAX_RESPONSE_MS) && (reciprocity == null || reciprocity >= t.TRUSTED_MIN_RECIPROCITY) && !tenseMajority(signals.toneCounts) && (daysSinceActivity == null || daysSinceActivity <= t.COLD_SILENCE_DAYS)) {
    reasons.push(REASON_KEYS.trustedSummary);
    return { tier: "trusted", reasons };
  }
  if (signals.medianResponseLatencyMs != null && signals.medianResponseLatencyMs <= t.HOT_MAX_RESPONSE_MS && (reciprocity == null || reciprocity >= t.TRUSTED_MIN_RECIPROCITY)) {
    reasons.push(REASON_KEYS.hotActive);
    return { tier: "hot", reasons };
  }
  reasons.push(REASON_KEYS.tepidDefault);
  return { tier: "tepid", reasons };
}
function capabilitiesFor(tier) {
  return tier === "trusted" ? [...CAPABILITIES] : [];
}
function hasContactCapability(tier) {
  return tier === "trusted";
}
function hasHistoryCapability(tier) {
  return tier === "trusted";
}

// src/engagement.ts
var ENGAGEMENT_VERSION = "engagement-v1";
var ENGAGEMENT_WEIGHTS = {
  recency: 0.3,
  // exp(-daysSinceActivity / 30d)
  reciprocity: 0.2,
  // existing direction-blind 0..1
  responsiveness: 0.2,
  // 1 - clamp(medianResponseLatencyMs / 3d)
  depth: 0.2,
  // min(completedExchanges / 10, 1) — capped, so volume saturates
  sincerity: 0.1
  // meanComposite (0..1)
};
var DAY2 = 24 * 60 * 60 * 1e3;
var RESPONSE_HORIZON_MS = 3 * DAY2;
var DEPTH_SATURATION = 10;
var MIN_PRESENT_WEIGHT = 0.5;
function engagementScore(s) {
  const terms = [];
  if (s.daysSinceActivity != null) {
    terms.push([ENGAGEMENT_WEIGHTS.recency, Math.exp(-s.daysSinceActivity / 30)]);
  }
  if (s.reciprocity != null) {
    terms.push([ENGAGEMENT_WEIGHTS.reciprocity, s.reciprocity]);
  }
  if (s.medianResponseLatencyMs != null) {
    terms.push([
      ENGAGEMENT_WEIGHTS.responsiveness,
      Math.max(0, 1 - s.medianResponseLatencyMs / RESPONSE_HORIZON_MS)
    ]);
  }
  terms.push([
    ENGAGEMENT_WEIGHTS.depth,
    Math.min(s.completedExchanges / DEPTH_SATURATION, 1)
  ]);
  if (s.meanComposite != null) {
    terms.push([ENGAGEMENT_WEIGHTS.sincerity, s.meanComposite]);
  }
  const weightSum = terms.reduce((acc, [w]) => acc + w, 0);
  if (weightSum < MIN_PRESENT_WEIGHT) return null;
  const value = terms.reduce((acc, [w, v]) => acc + w * v, 0) / weightSum;
  return Math.round(value * 100) / 100;
}
var ENGAGEMENT_BANDS = ["dormant", "steady", "flourishing"];
var BAND_THRESHOLDS = {
  dormant: 0.25,
  steady: 0.7
};
function bandFromScore(score) {
  if (score == null) return null;
  if (score < BAND_THRESHOLDS.dormant) return "dormant";
  if (score < BAND_THRESHOLDS.steady) return "steady";
  return "flourishing";
}

// src/relationship-metrics.ts
import { z as z2 } from "zod";
var RELATIONSHIP_METRICS_VERSION = "relationship-metrics-v1";
var RELATIONSHIP_QUESTION_KEYS = [
  "reciprocity.oneSided",
  "engagement.band",
  "trusted.adjudicate",
  "bio.emphasis"
];
function gatedRelationshipQuestionKeys(state) {
  const gated = [];
  if (state.letters.total < 3) gated.push("reciprocity.oneSided");
  if (state.letters.completedExchanges < 3) gated.push("trusted.adjudicate");
  return gated;
}
var MILESTONE_LETTERS = [10, 25, 50, 100];
function milestonesFor(letterCount) {
  return MILESTONE_LETTERS.filter((m) => letterCount >= m);
}
var MIN_LETTERS_FOR_DRIFT = 6;
var ToneCounts = z2.object({
  warm: z2.number().int().nonnegative(),
  neutral: z2.number().int().nonnegative(),
  tense: z2.number().int().nonnegative()
});
var RelationshipMetricsState = z2.object({
  relationshipId: z2.string().min(1),
  letters: z2.object({
    total: z2.number().int().nonnegative(),
    /** (user_low count, user_high count) — stable order; identities hidden. */
    byAuthor: z2.tuple([z2.number().int().nonnegative(), z2.number().int().nonnegative()]),
    completedExchanges: z2.number().int().nonnegative(),
    cancelled: z2.number().int().nonnegative().default(0),
    nudges: z2.number().int().nonnegative().default(0)
  }).strict(),
  pace: z2.object({
    medianResponseHours: z2.number().nonnegative().optional(),
    medianReadHours: z2.number().nonnegative().optional(),
    medianExchangeDays: z2.number().nonnegative().optional()
  }).strict().optional(),
  sincerity: z2.object({
    meanComposite: z2.number().min(0).max(1).nullable()
  }).strict().optional(),
  tone: z2.object({
    all: ToneCounts,
    firstHalf: ToneCounts,
    secondHalf: ToneCounts,
    /**
     * The per-letter tone verdicts in the order the letters were written —
     * the trajectory the drift question reads (added after the first live
     * run: aggregate halves alone measured 50%).
     */
    sequence: z2.array(z2.enum(["warm", "neutral", "tense"])).max(500).optional()
  }).strict().optional(),
  recency: z2.object({
    daysSinceLastLetter: z2.number().nonnegative()
  }).strict().optional()
}).strict();
var RELATIONSHIP_RUBRIC = [
  {
    key: "reciprocity.oneSided",
    kind: "noul",
    prompt: "Is this correspondence one-sided \u2014 is nearly all of the writing coming from one person rather than an exchange?"
  },
  {
    key: "engagement.band",
    kind: "score",
    prompt: "How alive is this correspondence right now? Consider how recently and how regularly letters move, whether replies are prompt, whether both sides write, and how sincere the letters are. Choose the level that best matches the overall rhythm.",
    // Order matters — the wire format is an ordered array of levels.
    // THREE levels, not four: the live runs measured 55% exact but 95–100%
    // within one level at four levels too — fine-grained exactness is not
    // Jev's resolution; three levels is the honest grain (§6.4, §9).
    criteria: {
      dormant: "Little or no recent activity; the correspondence has been quiet for a long stretch.",
      steady: "An ongoing back-and-forth \u2014 letters move at a regular, deliberate pace, whether plain or warm.",
      flourishing: "A warm, sincere, reciprocated exchange both sides keep investing in."
    }
  },
  {
    key: "trusted.adjudicate",
    kind: "noul",
    prompt: '"Trusted" status is granted only to a sustained, sincere, two-sided exchange. Judging from the signals below \u2014 waiting for an answer, one-sided writing, a predominantly tense tone, low sincerity \u2014 is there any CLEAR disqualifier that argues against "trusted" for this correspondence? Answer yes only on clear evidence.'
  },
  {
    key: "bio.emphasis",
    kind: "choice",
    prompt: "Which single theme would make the most honest one-line summary of this correspondence right now?",
    criteria: {
      warmth: "Letters that tend to be warm and kind.",
      longevity: "A correspondence that has lasted \u2014 many letters over a long time.",
      cadence: "Two people who reliably write back to each other, on a steady rhythm.",
      sincerity: "Letters that are personal and specific \u2014 written for each other."
    }
  }
];
function buildRelationshipQuestions() {
  const out = {};
  for (const q of RELATIONSHIP_RUBRIC) {
    if (q.kind === "noul") {
      out[q.key] = { type: "noul", instructions: q.prompt };
    } else if (q.kind === "score") {
      out[q.key] = {
        type: "score",
        instructions: q.prompt,
        criteria: Object.entries(q.criteria).map(([level, desc]) => ({ level, desc }))
      };
    } else {
      out[q.key] = { type: "choice", instructions: q.prompt, criteria: { ...q.criteria } };
    }
  }
  return out;
}
var RelationshipAnalysis = z2.object({
  relationshipId: z2.string().min(1),
  modelVersion: z2.string().min(1),
  judgements: z2.array(Judgement).max(50)
});
function parseRelationshipVerdicts(raw) {
  const result = RelationshipAnalysis.safeParse(raw);
  return result.success ? result.data.judgements : [];
}
var RELATIONSHIP_METRICS_CALIBRATION = {
  runAt: "2026-10-01",
  keys: {
    "reciprocity.oneSided": { status: "weighted", exactPct: 95, nearPct: 95 },
    "engagement.band": { status: "directional", exactPct: 55, nearPct: 100 },
    "trusted.adjudicate": { status: "quarantined", exactPct: 54, nearPct: 54 },
    "bio.emphasis": { status: "quarantined", exactPct: 56, nearPct: 56 }
  }
};
var BIO_EMPHASIS_THEMES = [
  "warmth",
  "longevity",
  "cadence",
  "sincerity"
];
var RELATIONSHIP_METRICS_COPY = {
  "engagement.band": {
    flourishing: "One of the people you write to most.",
    steady: "A friendship that keeps its rhythm.",
    dormant: "It's been quiet between you for a while."
  },
  "trusted.vetoed": "It isn't there yet \u2014 keep writing, and it will come.",
  "tone.drift.warmer": "The letters have been growing warmer.",
  "tone.drift.cooler": "The recent letters have felt cooler."
};
var isBand = (v) => ENGAGEMENT_BANDS.includes(v);
var isTheme = (v) => BIO_EMPHASIS_THEMES.includes(v);
function composeRelationshipMetrics(verdicts, opts) {
  const minConfidence = opts.minConfidence ?? DEFAULT_MIN_CONFIDENCE;
  const usable = usableJudgements(verdicts, minConfidence).filter(
    (j) => !(j.kind === "noul" && j.verdict === "unknown")
  );
  const byKey = new Map(usable.map((j) => [j.key, j]));
  const bandJ = byKey.get("engagement.band");
  const bandFromVerdict = bandJ && bandJ.kind === "score" && isBand(bandJ.verdict) ? bandJ.verdict : null;
  const bandFallback = bandFromVerdict == null;
  const band = bandFromVerdict ?? bandFromScore(opts.engagementScore);
  const oneSidedJ = byKey.get("reciprocity.oneSided");
  const driftJ = byKey.get("tone.drift");
  const adjudicateJ = byKey.get("trusted.adjudicate");
  const emphasisJ = byKey.get("bio.emphasis");
  const usedKeys = usable.map((j) => j.key).filter(
    (k) => RELATIONSHIP_QUESTION_KEYS.includes(k)
  );
  return {
    band,
    bandFallback,
    oneSided: oneSidedJ?.kind === "noul" ? oneSidedJ.verdict === "yes" : null,
    drift: driftJ?.kind === "noul" ? driftJ.verdict === "yes" ? "warmer" : "cooler" : null,
    trustedRuling: adjudicateJ?.kind === "noul" ? adjudicateJ.verdict === "no" ? "block" : "confirm" : null,
    emphasis: emphasisJ && emphasisJ.kind === "choice" && isTheme(emphasisJ.verdict) ? emphasisJ.verdict : null,
    // Degraded when any question was gated out, unusable, or the band fell back
    // to the numeric mapping (RELATIONSHIP_METRICS_V1.md §5).
    degraded: bandFallback || usedKeys.length < RELATIONSHIP_QUESTION_KEYS.length,
    usedKeys
  };
}

// src/letter-length.ts
var MIN_LETTER_CHARS = 200;
var MAX_LETTER_CHARS = 5e4;
function letterLength(body, required = MIN_LETTER_CHARS) {
  const length = body.trim().length;
  const remaining = Math.max(0, required - length);
  return {
    length,
    required,
    remaining,
    ratio: Math.min(1, required === 0 ? 1 : length / required),
    ready: remaining === 0
  };
}

// src/letter-quality.ts
var MAX_RUN_SHARE = 0.1;
var RUN_LENGTH = 5;
var MAX_TOP_WORD_SHARE = 0.25;
var MAX_REPEATED_PHRASE_SHARE = 0.3;
var MAX_GIBBERISH_SHARE = 0.3;
var MAX_WORD_LENGTH = 24;
var MESSAGES = {
  runs: "Some keys seem to have been held down. Letters are counted in words, not keystrokes.",
  "few-words": "This needs a few more different words before it can travel.",
  repetition: "Much of this letter repeats itself. Say a little more, in other words.",
  gibberish: "Parts of this don\u2019t read as words yet. Write it as you\u2019d want it read."
};
var WORD = /[\p{L}\p{N}]+(?:['’][\p{L}]+)*/gu;
var LATIN_WORD = /^[a-zà-ɏ'’]+$/;
var VOWELS = /[aeiouyàáâãäåæèéêëìíîïòóôõöøùúûüýÿœ]/;
var CONSONANT_RUN = /[^aeiouyàáâãäåæèéêëìíîïòóôõöøùúûüýÿœ'’]{6,}/;
function words(body) {
  return body.toLowerCase().match(WORD) ?? [];
}
function runShare(body) {
  const text = body.replace(/\s+/g, " ");
  let inRuns = 0;
  for (const m of text.matchAll(/(.)\1+/gu)) {
    if (m[0].length >= RUN_LENGTH && m[1] !== " ") inRuns += m[0].length;
  }
  return text.length === 0 ? 0 : inRuns / text.length;
}
function topWordShare(ws) {
  const counts = /* @__PURE__ */ new Map();
  let top = 0;
  for (const w of ws) {
    const n = (counts.get(w) ?? 0) + 1;
    counts.set(w, n);
    if (n > top) top = n;
  }
  return ws.length === 0 ? 0 : top / ws.length;
}
function repeatedPhraseShare(ws) {
  if (ws.length < 9) return 0;
  const seen = /* @__PURE__ */ new Set();
  let repeats = 0;
  const total = ws.length - 2;
  for (let i = 0; i < total; i++) {
    const key2 = `${ws[i]} ${ws[i + 1]} ${ws[i + 2]}`;
    if (seen.has(key2)) repeats++;
    else seen.add(key2);
  }
  return repeats / total;
}
function looksLikeMash(w) {
  if (w.length > MAX_WORD_LENGTH) return true;
  if (w.length < 4) return false;
  return !VOWELS.test(w) || CONSONANT_RUN.test(w);
}
function gibberishShare(ws) {
  const latin = ws.filter((w) => LATIN_WORD.test(w) || w.length > MAX_WORD_LENGTH);
  const total = latin.reduce((n, w) => n + w.length, 0);
  if (total < 20) return 0;
  return latin.filter(looksLikeMash).reduce((n, w) => n + w.length, 0) / total;
}
function fail(issue) {
  return { ok: false, issue, message: MESSAGES[issue] };
}
function letterQuality(body, config) {
  const text = body.trim();
  if (text.length === 0) return { ok: true };
  if (runShare(text) > MAX_RUN_SHARE) return fail("runs");
  const ws = words(text);
  if (config.minDistinctWords > 0 && new Set(ws).size < config.minDistinctWords) return fail("few-words");
  if (ws.length >= 20 && topWordShare(ws) > MAX_TOP_WORD_SHARE) return fail("repetition");
  if (repeatedPhraseShare(ws) > MAX_REPEATED_PHRASE_SHARE) return fail("repetition");
  if (gibberishShare(ws) > MAX_GIBBERISH_SHARE) return fail("gibberish");
  return { ok: true };
}

// src/app-settings.ts
import { z as z3 } from "zod";
var MINUTE = 60 * 1e3;
var HOUR2 = 60 * MINUTE;
var pad = (n) => String(n).padStart(2, "0");
var APP_SETTINGS_DEFAULTS = {
  /** Shortest a letter is held after collection, in minutes. */
  minHoldMinutes: DELIVERY_BOUNDS.MIN_HOLD_MS / MINUTE,
  /** Longest a letter is held after collection, in hours. */
  maxHoldHours: DELIVERY_BOUNDS.MAX_HOLD_MS / HOUR2,
  /** Hold for a pair with no rhythm yet (first letters), in hours. */
  defaultHoldHours: DEFAULT_HOLD_MS / HOUR2,
  /** Times of day (HH:MM, local to postTimezone) the post is collected. */
  collectionTimes: COLLECTION_WINDOWS.map((w) => `${pad(w.hour)}:${pad(w.minute)}`),
  /** Days with a collection round (0 = Sunday … 6 = Saturday). Deliveries run on the same days. */
  collectionDays: [...COLLECTION_DAYS].sort((a, b) => a - b),
  /** The delivery round (HH:MM, local to postTimezone): letters arrive only between these times. */
  deliveryStartTime: `${pad(DELIVERY_HOURS.start.hour)}:${pad(DELIVERY_HOURS.start.minute)}`,
  deliveryEndTime: `${pad(DELIVERY_HOURS.end.hour)}:${pad(DELIVERY_HOURS.end.minute)}`,
  /** IANA zone the collection rounds and delivery round are wall-clock times in. */
  postTimezone: POST_TIMEZONE,
  /** Characters a letter needs before it can be sealed. */
  minLetterChars: MIN_LETTER_CHARS,
  /** Longest nudge (P.S.) a sender can add to an unanswered letter. */
  nudgeMaxChars: 120,
  /** Turn off the repetition / gibberish checks on sealed letters (`letter-quality`). */
  letterQualityChecks: true,
  /** Days before a member's birthday their correspondents are reminded to write. 0 = no reminder. */
  birthdayRemindDaysBefore: 3,
  /** Different words a letter needs before it can be sealed. */
  minDistinctWords: 15,
  /** Longest single paste the composer accepts. 0 allows any paste. */
  maxPasteChars: 280,
  /** Postage classes (docs/POSTAGE_PLAN.md). Off = every letter goes as it did before. */
  postageEnabled: false,
  /** 1st class hold multiplier, in place of the tier factor. */
  firstClassFactor: 0.75,
  /** Express hold as a share of the same letter's 2nd-class hold. */
  expressHoldShare: 0.25,
  /** Longest an Express letter is ever held, in minutes. */
  expressMaxHoldMinutes: 120,
  /** Express stamps a qualifying pair earns each month, per member. */
  expressMonthlyAllowance: 2,
  /** Earned Express stamps stop accruing at this balance. */
  expressMaxBalance: 4,
  /** Lowest tier that earns 1st class. */
  firstClassMinTier: "hot",
  /** Lowest tier that earns Express. */
  expressMinTier: "trusted",
  /** Engagement (0..1) a pair needs for Express. */
  expressMinEngagement: 0.7,
  /** New people a member's lasting link (QR, story, bio) can start pairs with in a day. */
  lastingLinkDailyCap: 10,
  /** Welcome stamps an inviter can earn in a calendar month (abuse limit). */
  referralMonthlyRewardLimit: 10,
  /** Stamp books a toiyp Plus member can gift in a calendar month. 0 stops new gifts. */
  plusGiftsPerMonth: 1
};
var TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
var TIME_MSG = "times are HH:MM, 24-hour, in the post timezone";
var appSettingsSchema = z3.object({
  minHoldMinutes: z3.number().int().min(DELIVERY_BOUNDS.MIN_HOLD_MS / MINUTE).max(24 * 60),
  maxHoldHours: z3.number().min(1).max(24 * 14),
  defaultHoldHours: z3.number().min(0).max(24 * 14),
  collectionTimes: z3.array(z3.string().regex(TIME, TIME_MSG)).min(1).max(12).transform((ts) => [...new Set(ts)].sort()),
  collectionDays: z3.array(z3.number().int().min(0).max(6)).min(1, "at least one collection day").transform((ds) => [...new Set(ds)].sort((a, b) => a - b)),
  deliveryStartTime: z3.string().regex(TIME, TIME_MSG),
  deliveryEndTime: z3.string().regex(TIME, TIME_MSG),
  postTimezone: z3.string().refine(isValidTimeZone, "not a timezone this server knows (use an IANA name like Europe/London)"),
  minLetterChars: z3.number().int().min(0).max(MAX_LETTER_CHARS),
  nudgeMaxChars: z3.number().int().min(10).max(1e3),
  letterQualityChecks: z3.boolean(),
  birthdayRemindDaysBefore: z3.number().int().min(0).max(30),
  minDistinctWords: z3.number().int().min(0).max(200),
  maxPasteChars: z3.number().int().min(0).max(MAX_LETTER_CHARS),
  postageEnabled: z3.boolean(),
  firstClassFactor: z3.number().min(0.25).max(1),
  expressHoldShare: z3.number().min(0.05).max(1),
  expressMaxHoldMinutes: z3.number().int().min(DELIVERY_BOUNDS.MIN_HOLD_MS / MINUTE).max(24 * 60),
  expressMonthlyAllowance: z3.number().int().min(0).max(31),
  expressMaxBalance: z3.number().int().min(0).max(100),
  firstClassMinTier: z3.enum(RELATIONSHIP_TIERS),
  expressMinTier: z3.enum(RELATIONSHIP_TIERS),
  expressMinEngagement: z3.number().min(0).max(1),
  lastingLinkDailyCap: z3.number().int().min(1).max(1e3),
  referralMonthlyRewardLimit: z3.number().int().min(0).max(1e3),
  plusGiftsPerMonth: z3.number().int().min(0).max(31)
}).superRefine((s, ctx) => {
  if (s.maxHoldHours * 60 < s.minHoldMinutes) {
    ctx.addIssue({ code: "custom", path: ["maxHoldHours"], message: "longest hold is below the shortest" });
  }
  const def = s.defaultHoldHours * 60;
  if (def < s.minHoldMinutes || def > s.maxHoldHours * 60) {
    ctx.addIssue({
      code: "custom",
      path: ["defaultHoldHours"],
      message: "default hold must sit between the shortest and longest hold"
    });
  }
  if (s.deliveryEndTime <= s.deliveryStartTime) {
    ctx.addIssue({
      code: "custom",
      path: ["deliveryEndTime"],
      message: "the delivery round must end after it starts"
    });
  }
  if (s.expressMaxBalance < s.expressMonthlyAllowance) {
    ctx.addIssue({
      code: "custom",
      path: ["expressMaxBalance"],
      message: "the Express balance cap is below the monthly allowance"
    });
  }
});
function resolveAppSettings(stored) {
  const merged = { ...APP_SETTINGS_DEFAULTS, ...stored && typeof stored === "object" ? stored : {} };
  const parsed = appSettingsSchema.safeParse(merged);
  return parsed.success ? parsed.data : { ...APP_SETTINGS_DEFAULTS };
}
function letterQualityConfigOf(s) {
  return s.letterQualityChecks ? { minDistinctWords: s.minDistinctWords } : null;
}
function deliveryConfigOf(s) {
  return {
    minHoldMs: s.minHoldMinutes * MINUTE,
    maxHoldMs: s.maxHoldHours * HOUR2,
    defaultHoldMs: s.defaultHoldHours * HOUR2,
    collectionWindows: s.collectionTimes.map((t) => {
      const [hour, minute] = t.split(":").map(Number);
      return { hour, minute };
    }),
    collectionDays: s.collectionDays,
    deliveryStart: hm(s.deliveryStartTime),
    deliveryEnd: hm(s.deliveryEndTime),
    timeZone: s.postTimezone
  };
}
function hm(t) {
  const [hour, minute] = t.split(":").map(Number);
  return { hour, minute };
}
function postageConfigOf(s) {
  return {
    firstClassFactor: s.firstClassFactor,
    expressHoldShare: s.expressHoldShare,
    expressMaxHoldMs: s.expressMaxHoldMinutes * MINUTE,
    firstClassMinTier: s.firstClassMinTier,
    expressMinTier: s.expressMinTier,
    expressMinEngagement: s.expressMinEngagement
  };
}

// src/seals.ts
var SEALS = [
  { id: "dove", glyph: "\u{1F54A}", name: "Dove" },
  { id: "quill", glyph: "\u2712\uFE0F", name: "Quill" },
  { id: "anchor", glyph: "\u2693", name: "Anchor" },
  { id: "hourglass", glyph: "\u231B", name: "Hourglass" },
  { id: "scroll", glyph: "\u{1F4DC}", name: "Scroll" },
  { id: "key", glyph: "\u{1F5DD}\uFE0F", name: "Key" }
];
function isSealId(v) {
  return Boolean(v) && SEALS.some((s) => s.id === v);
}
function sealById(id) {
  return SEALS.find((s) => s.id === id);
}

// src/themes.ts
var RELATIONSHIP_THEMES = [
  "romantic",
  "family",
  "old-friend",
  "spiritual",
  "shared-craft",
  "everyday"
];
var THEME_META = {
  romantic: {
    label: "romantic",
    strong: ["sweetheart", "my dear", "love you", "miss you", "kiss", "hold you"],
    words: ["darling", "honey", "lover", "wedding", "engaged", "valentine", "romance", "admire", "beautiful", "dearest", "forever mine", "together again"]
  },
  family: {
    label: "family",
    strong: ["mum", "dad", "grandma", "grandad", "kids", "children"],
    words: ["mother", "father", "sister", "brother", "aunt", "uncle", "cousin", "family", "baby", "home", "house", "son", "daughter", "nephew", "niece"]
  },
  "old-friend": {
    label: "old friend",
    strong: ["remember when", "we used to", "old friend", "high school", "back then"],
    words: ["college", "school", "reminisce", "years ago", "that summer", "the good old days", "since we were", "childhood", "neighborhood", "teenage"]
  },
  spiritual: {
    label: "spiritual",
    strong: ["prayer", "church", "faith", "blessed", "god", "praying"],
    words: ["pray", "believe", "meditate", "meditation", "psalm", "gospel", "worship", "spirit", "pilgrimage", "sabbath", "monastery", "gratitude"]
  },
  "shared-craft": {
    label: "shared craft",
    strong: ["workshop", "garden", "recipe", "piano", "violin", "fishing"],
    words: ["gardening", "cooking", "baking", "paint", "painting", "wood", "pottery", "knitting", "sailing", "chess", "theatre", "photography", "machines", "brass", "tide tables"]
  },
  everyday: {
    label: "everyday",
    strong: [],
    words: ["groceries", "errands", "weather", "office", "commute", "rent", "landlord", "doctor", "dentist", "taxes", "insurance", "queue", "receipt", "letter box"]
  }
};
function themesOf(body) {
  const lower = body.toLowerCase();
  const tokens = lower.match(/[a-z']+/g) ?? [];
  const terms = new Set(tokens);
  for (let i = 0; i < tokens.length - 1; i++) {
    terms.add(`${tokens[i]} ${tokens[i + 1]}`);
  }
  const found = [];
  for (const theme of RELATIONSHIP_THEMES) {
    const meta = THEME_META[theme];
    let hits = 0;
    let strongHit = false;
    for (const word of meta.words) {
      if (terms.has(word)) hits++;
    }
    for (const word of meta.strong) {
      if (terms.has(word)) {
        strongHit = true;
        break;
      }
    }
    if (hits >= 2 || strongHit) found.push(theme);
  }
  return found;
}

// src/faces.ts
var FACE_IDS = [
  "face-1",
  "face-2",
  "face-3",
  "face-4",
  "face-5",
  "face-6",
  "face-7",
  "face-8",
  "face-9",
  "face-10",
  "face-11",
  "face-12"
];
var FACE_LABELS = {
  "face-1": "Hugo",
  "face-2": "Kofi",
  "face-3": "Noah",
  "face-4": "Elif",
  "face-5": "Mara",
  "face-6": "Mila",
  "face-7": "Ines",
  "face-8": "Jonas",
  "face-9": "Lin",
  "face-10": "Yuki",
  "face-11": "Tomas",
  "face-12": "Ada"
};
function isFaceId(value) {
  return FACE_IDS.includes(value);
}

// src/scrub.ts
var REDACTION_PLACEHOLDERS = {
  email: "[an email address \u2014 hidden until you know each other]",
  phone: "[a phone number \u2014 hidden until you know each other]",
  url: "[a link \u2014 hidden until you know each other]",
  address: "[a postal address \u2014 hidden until you know each other]",
  handle: "[a contact handle \u2014 hidden until you know each other]",
  payment: "[payment details \u2014 hidden until you know each other]"
};
var PATTERNS = [
  // Email addresses first — an address can look like a url to a looser rule.
  {
    kind: "email",
    re: /[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(?:\.[\w-]+)+/g
  },
  // Urls, including bare domains and mailto/wa.me contact links.
  {
    kind: "url",
    re: /\b(?:https?:\/\/|www\.)[^\s<>()]+|\b(?:mailto|tel):[^\s<>()]+|\b(?:wa\.me|whatsapp\.com\/send)\/?[^\s<>()]*/gi
  },
  // Bare domains, including two-label ones ("example.com"). The trailing label
  // must be a real TLD so ordinary dotted prose ("e.g", "3.5") is not caught.
  {
    kind: "url",
    re: /\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|me|app|dev|xyz|link|page|site|club|info|biz)\b(?:\/[^\s<>()]*)?/gi
  },
  // Label-led phone numbers: catches formats the bare-number rule misses.
  // The label is consumed by the match but never redacted — `applySpans`
  // rebuilds from the original text, so only group 1 (the number) is swapped.
  {
    kind: "phone",
    re: /\b(?:call|text|whatsapp|signal|phone|mobile|number|tel|reach me on)\b(?:[^.\n]{0,12}?)(\+?\d[\d\s().-]{6,}\d)/gi
  },
  // Grouped numbers with no label. Requires a separator AND enough digits to
  // look like a number rather than a quantity — the two shapes that qualify are
  // a country-coded number, or three-plus groups, or a five-digit group paired
  // with another (typical UK/US mobile). "3 cats", "25 dollars" and "14:20" all
  // fail at least one of those, so ordinary prose survives untouched.
  {
    kind: "phone",
    re: /(?<![\w-])(?:(?:\+\d{1,3}[\s.-])\d[\d\s().-]{6,}\d|\(\d{2,4}\)[\s.-]?\d{3}[\s.-]?\d{3,4}|\d{3,5}[\s.-]\d{3,4}[\s.-]?\d{0,4})(?![\w-])/g
  },
  // Label-led postal addresses: the label is kept, the street is the secret.
  // A street address needs either a street-type suffix or a postal code, so
  // "90210 was wrong" or "3 cats" are never mistaken for one.
  {
    kind: "address",
    re: /\b(?:address(?:ed)?|lives? at|based (?:at|in)|postal address)\b(?:[^.\n]{0,12}?)(\d+\s+[\w][\w\s,'-]{0,30}?\b(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|close|court|way|place|terrace|highway|hwy|boulevard|blvd)\b)/gi
  },
  // Postcode / zip on its own — a bare 5-digit run is only hidden when the word
  // "zip" or "postcode" introduces it.
  {
    kind: "address",
    re: /\b(?:zip code|zip|postcode)\b(?:[^.\n]{0,8}?)(\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b|\b\d{5}(?:-\d{4})?\b)/gi
  },
  // Money-transfer and wallet details: the highest-value thing to smuggle out.
  {
    kind: "payment",
    re: /\b(?:iban|bic|swift|sort code|routing number|account number|wallet|btc|eth)\b(?:[^.\n]{0,12}?)([\w-]{8,})/gi
  },
  // Off-platform handles, only where they look like a contact handle (@name).
  {
    kind: "handle",
    re: /(?<![\w@])@[a-z0-9][\w.-]{2,30}\b/gi
  }
];
function redactContactDetails(input) {
  const spans = collectSpans(input);
  const kinds = [...new Set(spans.map((s) => s.kind))];
  return { text: applySpans(input, spans), kinds, redacted: spans.length > 0 };
}
function collectSpans(input) {
  const spans = [];
  for (const { kind, re } of PATTERNS) {
    const rx = new RegExp(re.source, re.flags.includes("g") ? re.flags : `${re.flags}g`);
    for (let m = rx.exec(input); m; m = rx.exec(input)) {
      const groupText = m[1];
      const target = groupText && groupText.length > 0 ? groupText : m[0];
      const start = target === m[0] ? m.index : m.index + m[0].indexOf(target);
      spans.push({ start, end: start + target.length, kind });
    }
  }
  spans.sort((a, b) => a.start - b.start || b.end - a.end);
  const kept = [];
  for (const span of spans) {
    if (kept.some((k) => span.start < k.end && k.start < span.end)) continue;
    kept.push(span);
  }
  return kept;
}
function applySpans(input, spans) {
  let out = "";
  let cursor = 0;
  for (const span of spans) {
    out += input.slice(cursor, span.start) + REDACTION_PLACEHOLDERS[span.kind];
    cursor = span.end;
  }
  return out + input.slice(cursor);
}
function redactionNotice(kinds) {
  if (kinds.length === 0) return null;
  const unique = [...new Set(kinds)];
  const nouns = {
    email: "an email address",
    phone: "a phone number",
    url: "a link",
    address: "a postal address",
    handle: "a contact handle",
    payment: "payment details"
  };
  const list = unique.length === 1 ? nouns[unique[0]] : `${unique.slice(0, -1).map((k) => nouns[k]).join(", ")} and ${nouns[unique[unique.length - 1]]}`;
  return `The sender included ${list}, held back until you have exchanged a few letters. Reply as you would \u2014 if you come to trust each other, the details open up.`;
}

// src/personal.ts
var SIGNATURE_WIDTH = 320;
var SIGNATURE_HEIGHT = 120;
var SIGNATURE_MAX_CHARS = 12e3;
var clamp2 = (n, max) => Math.min(max, Math.max(0, n));
var fmt = (n) => String(Math.round(n * 10) / 10);
function signaturePath(strokes) {
  const parts = [];
  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    let last = null;
    const pts = [];
    for (const [rx, ry] of stroke) {
      const x = clamp2(rx, SIGNATURE_WIDTH);
      const y = clamp2(ry, SIGNATURE_HEIGHT);
      if (last && Math.hypot(x - last[0], y - last[1]) < 0.5) continue;
      pts.push(`${pts.length === 0 ? "M" : "L"}${fmt(x)} ${fmt(y)}`);
      last = [x, y];
    }
    if (pts.length === 1 && last) pts.push(`L${fmt(last[0] + 0.1)} ${fmt(last[1])}`);
    parts.push(pts.join(""));
  }
  return parts.join("");
}
var PATH_SHAPE = /^(?:[ML]-?\d{1,4}(?:\.\d)? -?\d{1,4}(?:\.\d)?)+$/;
function isSignaturePath(d) {
  return typeof d === "string" && d.length > 0 && d.length <= SIGNATURE_MAX_CHARS && d.startsWith("M") && PATH_SHAPE.test(d);
}
var CONTACT_NOTE_MAX_CHARS = 2e3;
var MET_PLACE_MAX_CHARS = 80;
var MET_EVENT_MAX_CHARS = 120;
var ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
function isMetDate(value, today = /* @__PURE__ */ new Date()) {
  if (!ISO_DAY.test(value)) return false;
  const d = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== value) return false;
  return value <= today.toISOString().slice(0, 10);
}
function normaliseMetTag(input) {
  const clean = (s, max) => {
    const t = (s ?? "").replace(/\s+/g, " ").trim();
    return t ? t.slice(0, max) : null;
  };
  const tag = {
    place: clean(input.place, MET_PLACE_MAX_CHARS),
    event: clean(input.event, MET_EVENT_MAX_CHARS),
    metOn: input.metOn ? input.metOn.trim() || null : null
  };
  return tag.place || tag.event || tag.metOn ? tag : null;
}
function metPhrase(tag) {
  const parts = [tag.event ? `at ${tag.event}` : null, tag.place ? `in ${tag.place}` : null].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join(", ") : null;
}
function isBirthday(value, today = /* @__PURE__ */ new Date()) {
  return isMetDate(value, today);
}
function birthdayMonthDay(birthday) {
  if (!birthday || !ISO_DAY.test(birthday)) return null;
  return birthday.slice(5);
}
function daysUntilBirthday(monthDay, today = /* @__PURE__ */ new Date()) {
  const m = /^(\d{2})-(\d{2})$/.exec(monthDay);
  if (!m) return null;
  const year = today.getUTCFullYear();
  const start = Date.UTC(year, today.getUTCMonth(), today.getUTCDate());
  const thisYear = Date.UTC(year, Number(m[1]) - 1, Number(m[2]));
  const nextYear = Date.UTC(year + 1, Number(m[1]) - 1, Number(m[2]));
  const target = thisYear >= start ? thisYear : nextYear;
  return Math.round((target - start) / 864e5);
}
var ACKNOWLEDGEMENT_COPY = "Got your letter \u2014 replying properly soon.";
function canAcknowledge(letter) {
  if (letter.acknowledgedAt) return false;
  if (letter.kind !== "letter") return false;
  return letter.state === "delivered" || letter.state === "opened";
}

// src/taste.ts
var MAX_COMPOSE_MS = 7 * 24 * 60 * 60 * 1e3;
var MIN_COMPOSE_MS = 6e4;
function letterLengthBand(chars) {
  if (chars < 600) return "a short note";
  if (chars < 2e3) return "a letter";
  if (chars < 6e3) return "a long letter";
  return "a very long letter";
}
function letterTimeBand(composeMs) {
  if (composeMs === null || composeMs === void 0 || !Number.isFinite(composeMs)) return null;
  if (composeMs < MIN_COMPOSE_MS) return null;
  if (composeMs < 30 * 6e4) return "written in under half an hour";
  if (composeMs < 4 * 60 * 6e4) return "written over half an hour";
  return "written over hours";
}
function clampComposeMs(ms) {
  if (ms === null || ms === void 0 || !Number.isFinite(ms)) return null;
  const rounded = Math.round(ms);
  if (rounded <= 0) return null;
  return Math.min(MAX_COMPOSE_MS, rounded);
}
function tasterPhrase(taster) {
  if (!taster) return null;
  const phrase = taster.time ? `${taster.length}, ${taster.time}` : taster.length;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

// src/postage.ts
var POSTAGE_CLASSES = ["second", "first", "express"];
var POSTAGE_SOURCES = ["standard", "earned", "purchased", "gifted"];
var CLASS_RULES_VERSION = "postage-v1";
var POSTAGE_RANK = { second: 0, first: 1, express: 2 };
var POSTAGE_LABELS = {
  second: "2nd class",
  first: "1st class",
  express: "Express"
};
var POSTAGE_STAMP_MARK = {
  second: "2nd",
  first: "1st",
  express: "EXP"
};
var MINUTE2 = 60 * 1e3;
var DEFAULT_POSTAGE_CONFIG = {
  firstClassFactor: 0.75,
  expressHoldShare: 0.25,
  expressMaxHoldMs: 120 * MINUTE2,
  firstClassMinTier: "hot",
  expressMinTier: "trusted",
  expressMinEngagement: 0.7
};
var REASON_KEYS2 = {
  firstHot: "postage.first.hot",
  firstTrusted: "postage.first.trusted",
  expressFlourishing: "postage.express.flourishing"
};
var POSTAGE_REASON_COPY = {
  [REASON_KEYS2.firstHot]: "You two write back within a day.",
  [REASON_KEYS2.firstTrusted]: "You're in trust \u2014 your letters have earned some weight.",
  [REASON_KEYS2.expressFlourishing]: "Your letters are flourishing \u2014 steady, warm and two-way."
};
var POSTAGE_LOCKED_HINT = "Comes with writing regularly.";
var POSTAGE_PAUSED_HINT = "Paused for now \u2014 it comes back when you're writing regularly again.";
function atLeast(tier, min) {
  return TIER_ORDER[tier] >= TIER_ORDER[min];
}
function postageEligibility(input, cfg = DEFAULT_POSTAGE_CONFIG) {
  const reasons = [];
  const first = atLeast(input.tier, cfg.firstClassMinTier);
  if (first) {
    reasons.push(input.tier === "trusted" ? REASON_KEYS2.firstTrusted : REASON_KEYS2.firstHot);
  }
  const express = atLeast(input.tier, cfg.expressMinTier) && input.engagement != null && input.engagement >= cfg.expressMinEngagement;
  if (express) reasons.push(REASON_KEYS2.expressFlourishing);
  const available = ["second"];
  if (first) available.push("first");
  if (express && input.expressBalance > 0) available.push("express");
  return {
    available,
    first,
    express,
    expressVisible: atLeast(input.tier, "hot"),
    reasons
  };
}
function bestEarnedClass(e) {
  if (e.express) return "express";
  if (e.first) return "first";
  return "second";
}
function postageTerms(cls, tier, cfg = DEFAULT_POSTAGE_CONFIG) {
  const secondFactor = Math.max(TIER_FACTORS[tier], 1);
  switch (cls) {
    case "first":
      return { classFactor: cfg.firstClassFactor, collection: "round", appliesToDefault: true };
    case "express":
      return {
        classFactor: secondFactor * cfg.expressHoldShare,
        collection: "immediate",
        maxHoldMs: cfg.expressMaxHoldMs,
        appliesToDefault: true
      };
    default:
      return { classFactor: secondFactor, collection: "round", appliesToDefault: false };
  }
}
function allowanceReason(now) {
  return `allowance:${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

// src/invite-links.ts
var INVITE_LINK_TTL_DAYS = 7;
var INVITE_LINK_MAX_USES = 5;

// src/stamps.ts
var STAMP_OWNERSHIP_SOURCES = ["plus", "gift", "admin"];
var STAMP_GIFT_STATES = ["waiting", "travelling", "held", "delivered", "withdrawn", "expired"];
var STAMP_GIFT_RETURNED_STATES = ["withdrawn", "expired"];
var GIFT_NOTE_MAX = 280;
var PLUS_LOCKED_NOTE = "Part of toiyp Plus";
function isPlus(plusUntil, now) {
  if (!plusUntil) return false;
  return new Date(plusUntil).getTime() > now.getTime();
}
function collectionAccess(collection, member) {
  if (!collection.active) return null;
  if (member.owned === "gift" || member.owned === "admin") return member.owned;
  if (!collection.plusOnly) return "free";
  if (member.plus || member.owned === "plus") return "plus";
  return "locked";
}
function canUseAccess(access) {
  return access != null && access !== "locked";
}
function giftMonthStart(now) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}
function nextGiftMonth(now) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}
function giftsLeft(member, givenThisMonth, perMonth) {
  if (!member.plus) return 0;
  return Math.max(0, perMonth - givenThisMonth);
}
export {
  ACKNOWLEDGEMENT_COPY,
  ALGORITHM_VERSION,
  APP_SETTINGS_DEFAULTS,
  BAND_THRESHOLDS,
  BIO_EMPHASIS_THEMES,
  BilateralHoldViolated,
  CAPABILITIES,
  CLASS_RULES_VERSION,
  COLLECTION_DAYS,
  COLLECTION_WINDOWS,
  CONTACT_NOTE_MAX_CHARS,
  ChoiceJudgement,
  DEFAULT_DELIVERY_CONFIG,
  DEFAULT_HOLD_MS,
  DEFAULT_MIN_CONFIDENCE,
  DEFAULT_POSTAGE_CONFIG,
  DELIVERY_BOUNDS,
  DELIVERY_HOURS,
  ENGAGEMENT_BANDS,
  ENGAGEMENT_VERSION,
  ENGAGEMENT_WEIGHTS,
  FACE_IDS,
  FACE_LABELS,
  FEATURE_KEYS,
  GIFT_NOTE_MAX,
  INVITE_LINK_MAX_USES,
  INVITE_LINK_TTL_DAYS,
  Judgement,
  LETTER_KINDS,
  LETTER_STATES,
  LETTER_TRANSITIONS,
  LETTER_TRIGGERS,
  LetterAnalysis,
  MAX_COMPOSE_MS,
  MAX_LETTER_CHARS,
  MET_EVENT_MAX_CHARS,
  MET_PLACE_MAX_CHARS,
  MILESTONE_LETTERS,
  MIN_LETTERS_FOR_DRIFT,
  MIN_LETTER_CHARS,
  NoulJudgement,
  OVERTURE_DECISIONS,
  PLUS_LOCKED_NOTE,
  POSTAGE_CLASSES,
  POSTAGE_LABELS,
  POSTAGE_LOCKED_HINT,
  POSTAGE_PAUSED_HINT,
  POSTAGE_RANK,
  POSTAGE_REASON_COPY,
  POSTAGE_SOURCES,
  POSTAGE_STAMP_MARK,
  POST_TIMEZONE,
  QUALITY_WEIGHTS,
  REDACTION_PLACEHOLDERS,
  RELATIONSHIP_METRICS_CALIBRATION,
  RELATIONSHIP_METRICS_COPY,
  RELATIONSHIP_METRICS_VERSION,
  RELATIONSHIP_QUESTION_KEYS,
  RELATIONSHIP_REASON_COPY,
  RELATIONSHIP_RUBRIC,
  RELATIONSHIP_THEMES,
  RELATIONSHIP_TIERS,
  RUBRIC,
  RelationshipAnalysis,
  RelationshipMetricsState,
  SCORE_LEVELS,
  SEALS,
  SETTLED_STATES,
  SIGNATURE_HEIGHT,
  SIGNATURE_MAX_CHARS,
  SIGNATURE_WIDTH,
  STAMP_GIFT_RETURNED_STATES,
  STAMP_GIFT_STATES,
  STAMP_OWNERSHIP_SOURCES,
  SYSTEM_LETTER_KINDS,
  ScoreJudgement,
  THEME_META,
  TIER_FACTORS,
  TIER_LABELS,
  TIER_ORDER,
  TIER_RULES_VERSION,
  TIER_THRESHOLDS,
  ToneCounts,
  addLocalDays,
  allowanceReason,
  appSettingsSchema,
  assertBilateralHold,
  bandFromScore,
  bestEarnedClass,
  birthdayMonthDay,
  buildRelationshipQuestions,
  canAcknowledge,
  canUseAccess,
  capabilitiesFor,
  clampComposeMs,
  classifyTier,
  collectionAccess,
  composeRelationshipMetrics,
  computeComposite,
  computeDeliveryWindow,
  daysUntilBirthday,
  deliveryConfigOf,
  engagementScore,
  explain,
  explainNumeric,
  gatedRelationshipQuestionKeys,
  giftMonthStart,
  giftsLeft,
  hasContactCapability,
  hasHistoryCapability,
  isBirthday,
  isFaceId,
  isFeatureKey,
  isMetDate,
  isOvertureClosed,
  isOvertureDecided,
  isOvertureKind,
  isPastLastPost,
  isPeekable,
  isPlus,
  isSealId,
  isSignaturePath,
  isSystemLetterKind,
  isTerminal,
  isValidTimeZone,
  isVisibleToRecipient,
  judgementValue,
  letterLength,
  letterLengthBand,
  letterQuality,
  letterQualityConfigOf,
  letterTimeBand,
  metPhrase,
  milestonesFor,
  nextCollectionTime,
  nextDeliveryTime,
  nextGiftMonth,
  nextState,
  normaliseMetTag,
  numericSignals,
  parseJudgements,
  parseRelationshipVerdicts,
  postageConfigOf,
  postageEligibility,
  postageTerms,
  redactContactDetails,
  redactionNotice,
  resolveAppSettings,
  sealById,
  signaturePath,
  tasterPhrase,
  themesOf,
  usableJudgements,
  wallClock,
  zonedInstant
};
